<template>
    <div class="availability-page">
        <visit-summary
            :stay-days="stayDays"
            :pets="Number(guest.pets) > 0"
            :submitted="complete"
            class="section"
            @house="houseChanged"
            @show-date-chooser="showDateChooser = true"
        />

        <date-chooser
            v-if="showDateChooser"
            :house="showDateChooser ? house : null"
            :date-in="dateIn"
            :date-out="dateOut"
            @cancel="showDateChooser = false"
            @done="datesChosen"
        />

        <template v-else-if="showReservationRequestForm && !complete">
            <reservation-request-guest
                :house-label="house.name"
                :max-guests="house.config.maxGuests"
                :validator="$v.guest"
                style="margin-top: 40px;"
            />

            <reservation-request-agreements
                :agreements="agreements"
                :date-payment-due="datePaymentDue"
                :validator="$v.agreements"
            />

            <reservation-request-payment
                :stripe-key="stripeKey"
                @valid="paymentInfoComplete = $event"
                ref="payment"
            />

            <reservation-request-confirm
                :details="summaryDetails"
                :is-deposit="isDeposit"
                :may-submit="maySubmit"
                :ajax-message="ajax.message"
                @submit-request="submitRequest"
            />
        </template>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import { validationMixin } from 'vuelidate';
import { required, requiredIf, integer, email, minValue, between } from 'vuelidate/lib/validators';
import * as calcs from '../../../_modules/reservationCalcs';

import DateChooser from './DateChooser.vue';
import MonthGrid from './MonthGrid.vue';
import VisitSummary from './VisitSummary.vue';

import axios from 'axios';
import swal from 'sweetalert';
import ReservationRequestAgreements from './ReservationRequest/ReservationRequest-Agreements.vue';
import ReservationRequestConfirm from './ReservationRequest/ReservationRequest-Confirm.vue';
import ReservationRequestGuest from './ReservationRequest/ReservationRequest-Guest.vue';
import ReservationRequestPayment from './ReservationRequest/ReservationRequest-Payment.vue';

const truthy = (value) => !! value;
const agreedIfRequired = (agreement) => {
    return !agreement.required || agreement.agreed;
}

dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);

// const taxRate = 0.09;
export default {
    components: {
        // ReservationRequest,
        ReservationRequestAgreements,
        ReservationRequestConfirm,
        ReservationRequestGuest,
        ReservationRequestPayment,
        DateChooser,
        MonthGrid,
        VisitSummary,
    },

    mixins: [
        validationMixin,
    ],

    props: {
        snippets: {
            type: Object,
        },
        stripeKey: {
            type: String,
        },
    },

    data() {
        return {
            house: null,

            showDateChooser: false,

            stayDays: [],

            guest: {
                name: process.env.MIX_APP_ENV === 'local' ? 'JR Sandin' : '',
                email: process.env.MIX_APP_ENV === 'local' ? 'jr@mdimaine.us' : '',
                phone: process.env.MIX_APP_ENV === 'local' ? '909-556-9542' : '',
                adults: process.env.MIX_APP_ENV === 'local' ? '2' : '',
                children: process.env.MIX_APP_ENV === 'local' ? '0' : '',
                pets: process.env.MIX_APP_ENV === 'local' ? '0' : '',
                comments: '',
            },

            agreements: {
                rental: {
                    // show: false,
                    // viewed: true,
                    agreed: false,
                    required: true,
                },
                pet: {
                    // show: false,
                    // viewed: true,
                    agreed: false,
                    required: false,
                },
            },

            paymentInfoComplete: false,

            complete: false,

            myEarliestMonthToShow: null,

            ajax: {
                loading: false,
                message: '',
            },
        };
    },

    validations: {
        guest: {
            name: { required },
            email: { required, email },
            phone: { required },
            adults: { required, integer, minValue: minValue(1) },
            children: { required, integer, minValue: minValue(0) },
            pets: { required, integer, minValue: between(0, 2) },
            comments: {},
        },

        agreements: {
            rental: { agreedIfRequired },
            pet: { agreedIfRequired },
        },

        paymentInfoComplete: { truthy },
    },

    computed: {
        showReservationRequestForm() {
            return this.stayDays.length > 0;
        },

        dateIn() {
            return this.stayDays.length
                ? this.stayDays[0].date
                : null
            ;
        },

        dateOut() {
            return this.stayDays.length
                ? dayjs(this.stayDays[this.stayDays.length - 1].date).add(1, 'day').format('YYYY-MM-DD')
                : null
            ;
        },

        datePaymentDue() {
            if (this.dateIn === null) {
                return null;
            }
            const thirtyDays = dayjs(this.dateIn).subtract(30, 'd').startOf('day');
            return (thirtyDays.isAfter(dayjs(), 'days'))
                ? thirtyDays.startOf('day')
                : null
            ;
        },

        depositDue() {
            return this.isDeposit
                ? calcs.deposit(this.stayDays, this.guest.pets)
                : calcs.total(this.stayDays, this.guest.pets)
            ;
        },

        isDeposit() {
            return dayjs(this.dateIn).subtract(31, 'd').startOf('day').isAfter(dayjs(), 'days');
        },

        summaryDetails() {
            console.debug('AvailabilityPage: update summaryDetails');
            return {
                house: this.house,
                dateIn: this.dateIn,
                dateOut: this.dateOut,
                rent: this.discountedRent,
                tax: calcs.tax(this.stayDays, this.guest.pets),
                total: calcs.deposit(this.stayDays, this.guest.pets),
                paymentDue: this.depositDue,
                isDeposit: this.isDeposit,
                finalPayment: calcs.total(this.stayDays, this.guest.pets) - this.depositDue,
                finalPaymentDate: this.datePaymentDue,
            }
        },

        maySubmit() {
            return !this.$v.$invalid && !this.ajax.loading;
        },

        discountedRent() {
            return calcs.rent(this.stayDays, this.guest.pets) - calcs.discount(this.stayDays, this.guest.pets);
        },
    },

    watch: {
        'guest.pets': {
            handler: function (val) {
                this.agreements.pet.required = val > 0;
            },
            immediate: true,
        },
    },

    methods: {
        houseChanged(house_slug) {
            this.loadHouse(house_slug)
                .then(() => {
                    this.showDateChooser = true;
                })
            ;
        },

        datesChosen(stayDays) {
            console.debug('datesChosen', stayDays);
            this.stayDays.splice(0, this.stayDays.length, ...stayDays);
            this.showDateChooser = false;
        },

        submitRequest() {
            if (!this.maySubmit) {
                return;
            }

            let vue = this;
            this.ajax.loading = true;

            let promises = [];
            let stripeToken = null;

            if (this.$refs.payment.method === 'credit') {
                this.ajax.message = 'Contacting payment processor...';
                promises.push(
                    this.$refs.payment.$refs.credit.getToken()
                        .then(function (result) {
                            if (result.error) {
                                console.error(result.error);
                                axios.post('/api/log', {message: 'Problem creating token'});
                                axios.post('/api/log', {data: btoa(JSON.stringify(result.error))});
                                return null;
                            }
                            stripeToken = result.token.id;
                            return true;
                        })
                        .catch(function (error) {
                            console.error(error);
                            axios.post('/api/log', {message: 'Problem creating token:'});
                        })
                );
            }

            Promise.all(promises)
                .then(function() {
                    vue.ajax.message = 'Saving reservation details...';
                    axios.post('/api/reservation-request', {
                        // reserve_hashid: vue.reserve && vue.reserve.hashid,
                        guest: vue.guest,
                        // REFACTOR wrap next 4 items in blocks[]
                        house_slug: vue.house.slug,
                        date_in: vue.dateIn,
                        date_out: vue.dateOut,
                        rent: vue.discountedRent,

                        agreements: vue.agreements,
                        paymentMethod: vue.$refs.payment.method,
                        stripeToken: stripeToken,
                        // stripeLiveMode: stripeLiveMode,
                        // 'h-captcha-response': document.querySelector('textarea[name="h-captcha-response"]').value,
                    })
                        .then(function() {
                            vue.complete = true;
                        })
                        .catch(function (error) {
                            console.error(error);
                            let message = (error.response && error.response.data && error.response.data.message)
                                ? error.response.data.message
                                : 'Server error.'
                            ;
                            axios.post('/api/log', {message: 'Problem processing reservation request.'});
                            axios.post('/api/log', {data: btoa(JSON.stringify({
                                status: (error.response && error.response.status) || 'null',
                                message: message,
                                guest: vue.guest,
                                weeks: vue.weeks,
                                agreements: vue.agreements,
                                stripeToken: stripeToken,
                            }))});
                            swal('Oops', 'There was a problem sending your reservation request. Please contact us by email at info@derbylanecottage.com.', 'error');
                        })
                        .finally(function() {
                            vue.ajax.loading = false;
                        })
                    ;
                })
            ;
        },

        loadHouse(house_slug) {
            this.stayDays.splice(0, this.stayDays.length);
            return axios.get('/api/houses/' + house_slug)
                .then(async (response) => {
                    this.house = Object.assign({}, response.data);
                })
        },

    },
}
</script>

<style lang="scss">
.availability-page {
    .section {
        border: 1px solid black;
        border-radius: 15px;
        background-color: white;
        padding: 20px;
        width: 80vw;
        max-width: 800px;
        margin: 0 auto 20px auto;

        & > * {
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
        }

        @media(max-width: 600px) {
            width: 96vw;
            padding: 10px;
        }
    }

    p {
        max-width: 560px;
        margin: auto;
    }
}
</style>
